import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { devLog } from "../lib/devLog";
import { useAuth } from "../contexts/AuthContext";
import { usePolling } from "../lib/usePolling";
import { Navbar } from "../components/Navbar";
import { CalendarBlank, FolderOpen, Lightning, CheckCircle, Hourglass, Eye } from "@phosphor-icons/react";
import { STATUS_LABEL } from "../lib/labels";

const STATUS_META = {
  todo: { label: STATUS_LABEL.todo, color: "#71717A", icon: Hourglass },
  doing: { label: STATUS_LABEL.doing, color: "#007AFF", icon: Lightning },
  with_editor: { label: STATUS_LABEL.with_editor, color: "#A855F7", icon: Lightning },
  to_be_checked: { label: STATUS_LABEL.to_be_checked, color: "#F59E0B", icon: Eye },
  checked: { label: STATUS_LABEL.checked, color: "#10B981", icon: CheckCircle },
  done: { label: STATUS_LABEL.done, color: "#22C55E", icon: CheckCircle },
};

const ROLE_HEADLINE = {
  worker: { title: "My queue.", subtitle: "Every task assigned to you across every project. Focus on With Producer first." },
  checker: { title: "Review desk.", subtitle: "All tasks waiting for your copy edit, in one place." },
  video_editor: { title: "Editor bay.", subtitle: "All clips producers have sent over for editing." },
  graphic_designer: { title: "GFX bench.", subtitle: "Every task With Video Editor that could use your graphics." },
  publisher: { title: "Publish desk.", subtitle: "Everything Ready to be Published, plus what you've shipped." },
  assigner: { title: "Pipeline.", subtitle: "Every task you've assigned, tracked across all projects." },
  admin: { title: "Pipeline.", subtitle: "Every task you've assigned, tracked across all projects." },
};

export default function Workload() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/me/workload");
      setData(res.data);
    } catch (e) {
      devLog.error("[Workload] load failed", e);
      /* keep last good data on transient errors */
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live updates without refresh
  usePolling(load, 8000);

  const headline = ROLE_HEADLINE[user?.role] || ROLE_HEADLINE.worker;
  const tasks = data?.tasks || [];

  // Group by status preserving the sort order from backend
  const groups = {};
  for (const t of tasks) {
    if (!groups[t.status]) groups[t.status] = [];
    groups[t.status].push(t);
  }
  const STATUS_ORDER = ["doing", "with_editor", "to_be_checked", "todo", "checked", "done"];

  const inProgressCount = (groups.doing?.length || 0) + (groups.with_editor?.length || 0) + (groups.to_be_checked?.length || 0);

  return (
    <div>
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-10">
          <p className="label-mono mb-2">Workload</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-none">{headline.title}</h1>
          <p className="text-[color:var(--text-secondary)] mt-3 max-w-2xl">{headline.subtitle}</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <StatCard label="In progress" value={inProgressCount} accent="#007AFF" />
          <StatCard label="Up next" value={groups.todo?.length || 0} accent="#71717A" />
          <StatCard label="Awaiting check" value={groups.to_be_checked?.length || 0} accent="#F59E0B" />
          <StatCard label="Completed" value={(groups.done?.length || 0) + (groups.checked?.length || 0)} accent="#22C55E" />
        </div>

        {loading ? (
          <p className="text-[color:var(--text-tertiary)]">Loading workload...</p>
        ) : tasks.length === 0 ? (
          <div className="surface p-16 rounded-md text-center">
            <FolderOpen size={48} weight="duotone" className="text-[color:var(--text-tertiary)] mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">Nothing on your plate</h3>
            <p className="text-[color:var(--text-secondary)]">You're all caught up. Enjoy the breather.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {STATUS_ORDER.filter((s) => groups[s]?.length).map((status) => {
              const cfg = STATUS_META[status];
              const Icon = cfg.icon;
              return (
                <section key={status} data-testid={`workload-section-${status}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full" style={{ background: cfg.color }} />
                    <h2 className="label-mono" style={{ color: cfg.color }}>{cfg.label}</h2>
                    <span className="text-xs font-mono text-[color:var(--text-tertiary)]">· {groups[status].length}</span>
                  </div>
                  <div className="space-y-2">
                    {groups[status].map((t) => {
                      const due = t.due_date ? new Date(t.due_date) : null;
                      const overdue = due && due < new Date() && t.status !== "done";
                      return (
                        <Link
                          key={t.id}
                          to={`/projects/${t.project_id}`}
                          data-testid={`workload-task-${t.id}`}
                          className="surface-2 rounded-md px-5 py-4 flex items-center justify-between gap-4 hover:bg-[color:var(--surface-hover)] transition-all duration-200 hover:-translate-y-0.5"
                        >
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            <Icon size={20} weight="duotone" style={{ color: cfg.color }} className="shrink-0" />
                            <div className="min-w-0">
                              <p className="font-semibold truncate">{t.title}</p>
                              <p className="text-xs text-[color:var(--text-secondary)] mt-0.5 font-mono">
                                {t.project_name}
                                {due && (
                                  <span className={overdue ? "text-red-400 ml-3" : "ml-3"}>
                                    <CalendarBlank size={11} className="inline mr-1" />
                                    {due.toLocaleDateString()}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <span className="label-mono shrink-0 hidden sm:block" style={{ color: cfg.color }}>{cfg.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="surface p-4 rounded-md">
      <p className="label-mono text-[10px]" style={{ color: accent }}>{label}</p>
      <p className="font-heading text-3xl font-black tracking-tighter mt-1">{value}</p>
    </div>
  );
}
